export interface RepairRecordPaymentLike {
  payed: boolean;
  payedValue: number;
  deleted?: boolean;
}

export interface FinishedRepairRecordPaymentLike {
  payed_value: number;
}

export interface RepairRecordPricingLike {
  payedValue: number;
  finishedAt?: Date | null;
  pericia: {
    cashPayedAmount: number;
  };
}

export interface RepairRecordPaymentDifference {
  shouldDisplayMessage: boolean;
  amountToDisplay: number;
  status: "balanced" | "below" | "above";
}

export const isRepairRecordPayable = (record: RepairRecordPaymentLike): boolean => {
  return !record.deleted && !record.payed;
};

export const calculatePayedRepairRecordsTotal = (records: RepairRecordPaymentLike[]): number => {
  return records.reduce((total, record) => {
    if (record.deleted || !record.payed) {
      return total;
    }

    return total + record.payedValue;
  }, 0);
};

export const calculateRepairRecordsAmountToPay = (records: RepairRecordPaymentLike[]): number => {
  return records.reduce((total, record) => {
    if (!isRepairRecordPayable(record)) {
      return total;
    }

    return total + record.payedValue;
  }, 0);
};

export const calculateFinishedRepairRecordsPayedValue = (records: FinishedRepairRecordPaymentLike[]): number => {
  return records.reduce((total, record) => total + record.payed_value, 0);
};

export const calculateRepairRecordPaymentDifference = (
  expectedAmount: number,
  records: FinishedRepairRecordPaymentLike[]
): RepairRecordPaymentDifference => {
  const actualAmount = calculateFinishedRepairRecordsPayedValue(records);
  const amountToDisplay = expectedAmount - actualAmount;

  if (amountToDisplay === 0) {
    return {
      shouldDisplayMessage: false,
      amountToDisplay: 0,
      status: "balanced"
    };
  }

  return {
    shouldDisplayMessage: true,
    amountToDisplay,
    status: amountToDisplay > 0 ? "below" : "above"
  };
};

export const calculateTechnicianPaymentShare = (amount: number, repairRecordCount: number): number => {
  if (repairRecordCount <= 0) {
    return 0;
  }

  return amount / repairRecordCount;
};

export const isRepairRecordMissingPayment = (record: RepairRecordPricingLike): boolean => {
  return record.pericia.cashPayedAmount === 0 && record.payedValue === 0;
};

export const getRepairRecordDisplayPayment = (record: RepairRecordPricingLike): number => {
  if (record.pericia.cashPayedAmount > 0) {
    return record.pericia.cashPayedAmount;
  }

  return record.payedValue;
};

export const filterRepairRecordsMissingPayment = <T extends RepairRecordPricingLike>(records: T[]): T[] => {
  return records.filter(isRepairRecordMissingPayment);
};

export const sortRepairRecordsByFinishedAtDesc = <T extends RepairRecordPricingLike>(records: T[]): T[] => {
  return [...records].sort((a, b) => {
    const aTime = a.finishedAt ? a.finishedAt.getTime() : Number.POSITIVE_INFINITY;
    const bTime = b.finishedAt ? b.finishedAt.getTime() : Number.POSITIVE_INFINITY;
    return bTime - aTime;
  });
};

export const getRepairRecordsByAdminFilter = <T extends RepairRecordPricingLike>(records: T[], filterBy: "price" | "date"): T[] => {
  if (filterBy === "price") {
    return filterRepairRecordsMissingPayment(records);
  }

  return sortRepairRecordsByFinishedAtDesc(records);
};
